// src/services/socket.service.ts
// Socket.IO client for the admin panel. Connects once on init, auto-reconnects,
// and emits "notification:new" events consumed by the NotificationBell component.

import { io, Socket } from "socket.io-client";
import { getApiBaseUrl } from "../utils/apiConfig";

let socket: Socket | null = null;
const listeners: Map<string, Set<(...args: any[]) => void>> = new Map();

function getToken(): string | null {
  return localStorage.getItem("token");
}

export function connectSocket(): void {
  if (socket?.connected) return;

  const token = getToken();
  if (!token) return;

  // getApiBaseUrl() ends in /api/v1 (the REST base). socket.io-client would use
  // that as the socket path and the handshake 404s (backend serves /socket.io/).
  // Strip the API path before connecting, exactly like the mobile apps do.
  socket = io(getApiBaseUrl().replace(/\/api\/v1\/?$/, ""), {
    auth: { token },
    transports: ["websocket"],
    reconnectionAttempts: 10,
    reconnectionDelay: 3000,
  });

  socket.on("connect", () => {
    console.log("[Socket] Admin panel connected:", socket?.id);
  });

  socket.on("connect_error", (err) => {
    console.warn("[Socket] Connect error:", err.message);
  });

  // Replay all registered listeners after reconnect
  socket.onAny((event, ...args) => {
    const cbs = listeners.get(event);
    if (cbs) cbs.forEach((cb) => cb(...args));
  });
}

export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
}

/** Subscribe to a socket event. Returns an unsubscribe function. */
export function onSocketEvent<T = any>(event: string, cb: (data: T) => void): () => void {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event)!.add(cb);

  return () => {
    listeners.get(event)?.delete(cb);
  };
}

/** Update the auth token (called after token refresh) and reconnect if needed. */
export function refreshSocketAuth(): void {
  if (!socket) return;
  const token = getToken();
  if (!token) return;
  // Reconnect with the new token
  socket.auth = { token };
  socket.disconnect().connect();
}
